import { Route } from '@/app/router'
import store from '@/packages/vuex'

const Billing = () => import('./index.vue')
const Bills = () => import('./components/Bills/index.vue')
const BillingRecords = () => import('./components/BillingRecords/index.vue')
const BillingSeriesPolicies = () => import('./components/BillingSeriesPolicies/index.vue')
const BillingCashRegisters = () => import('./components/BillingCashRegisters/index.vue')
const BillingDocumentConfigs = () => import('./components/BillingDocumentConfigs/index.vue')
const BillingItemDescriptionGroups = () => import('./components/BillingItemDescriptionGroups/index.vue')
const BillingItemDescriptionGroup = () => import('./components/BillingItemDescriptionGroup/index.vue')
const BillingAccountingExport = () => import('./components/BillingAccountingExport/index.vue')

const BillingRecord = () => import('./components/BillingRecord/index.vue')

const billingMeta = {
	sort: 16,
	displayInSidebar: true,
	displayName: 'billing',
	icon: 'fal file-invoice-dollar',
	activeIcon: 'fas file-invoice-dollar'
}

const commonProps = ['page', 'perPage']

const billingBasePath = '/billing'
const billingPermissions = ['VIEW_SOFTWARE_BILL']

const getBillingRoutePermissions = route => route._permissions
	|| route._meta?.billingPermissions
	|| route._meta?.permissions

const getBillingPermissionMethod = route => route._meta?.permissionMethod || 'hasSomePermission'

const getBillingPermissionMatch = route => route._meta?.permissionMatch === 'some' ? 'some' : 'every'

const hasBillingRoutePermissions = (user, permissions, method, match) => {
	if (!permissions || permissions.length === 0) return true
	return permissions[match](permission => user?.[method]?.(permission))
}

const getBillingRedirect = (_to, _from) => {
	const user = store.getters['Auth/user']
	if (!user) return `${billingBasePath}/records`

	const currentPath = _to?.path?.replace(billingBasePath, '') || ''

	const childRoutes = billingChildren.filter(r => {
		const path = r._path || r.path
		return path && path !== '/' && !path.includes(':')
	})

	// Find first accessible route (excluding current)
	for (const childRoute of childRoutes) {
		const permissions = getBillingRoutePermissions(childRoute)
		const path = childRoute._path || childRoute.path
		const normalizedPath = path.startsWith('/') ? path : `/${path}`

		if (normalizedPath === currentPath) continue

		if (!permissions || permissions.length === 0) {
			return `${billingBasePath}${normalizedPath}`
		}
		const method = getBillingPermissionMethod(childRoute)
		const match = getBillingPermissionMatch(childRoute)
		if (hasBillingRoutePermissions(user, permissions, method, match)) {
			return `${billingBasePath}${normalizedPath}`
		}
	}

	// Check if current route is accessible
	for (const childRoute of childRoutes) {
		const permissions = getBillingRoutePermissions(childRoute)
		const path = childRoute._path || childRoute.path
		const normalizedPath = path.startsWith('/') ? path : `/${path}`

		if (normalizedPath === currentPath) {
			if (!permissions || permissions.length === 0) {
				return null
			}
			const method = getBillingPermissionMethod(childRoute)
			const match = getBillingPermissionMatch(childRoute)
			if (hasBillingRoutePermissions(user, permissions, method, match)) {
				return null
			}
		}
	}

	// Fallback to records route if accessible, otherwise let middleware redirect to Home
	const recordsRoute = childRoutes.find(r => {
		const p = r._path || r.path
		return p === 'records' || p === '/records'
	})
	if (recordsRoute) {
		const permissions = getBillingRoutePermissions(recordsRoute)
		const method = getBillingPermissionMethod(recordsRoute)
		const match = getBillingPermissionMatch(recordsRoute)
		if (!permissions || permissions.length === 0 || hasBillingRoutePermissions(user, permissions, method, match)) {
			return `${billingBasePath}/records`
		}
	}

	return null
}

// Build child routes array for permission checking
const billingChildren = [
	new Route('/').redirect(getBillingRedirect),
	new Route('bills', Bills).as('Dashboard:Billing:Bills').props(commonProps).permissions(billingPermissions).meta({ permissionMethod: 'hasPermission', permissionRedirect: getBillingRedirect }),
	new Route('records', BillingRecords).as('Dashboard:Billing:Records').permissions(['VIEW_BILLING']).props(commonProps),
	new Route('records/:billingInvoiceId', BillingRecord).as('Dashboard:Billing:Record').permissions(['VIEW_BILLING']).props(commonProps),
	new Route('series-policies', BillingSeriesPolicies).as('Dashboard:Billing:SeriesPolicies').permissions(['VIEW_BILLING']).props(commonProps),
	new Route('cash-registers', BillingCashRegisters).as('Dashboard:Billing:CashRegisters').permissions(['VIEW_BILLING']).props(commonProps),
	new Route('document-configs', BillingDocumentConfigs).as('Dashboard:Billing:DocumentConfigs').permissions(['VIEW_BILLING']).props(commonProps),
	new Route('item-description-groups', BillingItemDescriptionGroups).as('Dashboard:Billing:ItemDescriptionGroups').permissions(['VIEW_BILLING']).props(commonProps),
	new Route('item-description-groups/:groupId', BillingItemDescriptionGroup).as('Dashboard:Billing:ItemDescriptionGroup').permissions(['VIEW_BILLING']).props(commonProps),
	new Route('export', BillingAccountingExport).as('Dashboard:Billing:Export').permissions(['VIEW_BILLING']).props(commonProps)
]

export default new Route('/billing', Billing)
	.meta({
		...billingMeta,
		permissionRedirect: getBillingRedirect,
		permissionMatch: 'some'
	})
	.props(commonProps)
	.permissions(['VIEW_SOFTWARE_BILL', 'VIEW_BILLING'])
	.children(billingChildren)

export { billingChildren }
